import React from 'react';
import { MdOutlineForest } from 'react-icons/md';
import { SectionCard, Pill, Note, StatTile } from './ui';
import { fmtNum, fmtPct, fmtSqft, fmtDateTime, safeArray, isNum, DASH } from '../format';

/**
 * Tree canopy (property-context 2.2.0): parcel tree cover from the 1 m
 * canopy-height model. Everything here is a canopy patch measurement — never a
 * tree count — and the height classes are model estimates for the imagery year.
 */

// Three height classes, young → mature, drawn as one stacked bar. The greens
// darken with height so the bar reads the same way the map layer does.
export const HEIGHT_CLASSES = [
  { key: '3_5_m', label: '3–5 m', name: 'young', color: '#86efac' },
  { key: '5_15_m', label: '5–15 m', name: 'established', color: '#22c55e' },
  { key: 'over_15_m', label: '>15 m', name: 'mature', color: '#166534' },
];

export const CANOPY_ATTRIBUTION = 'Canopy height: Meta/WRI 1 m global canopy height (CC BY 4.0)';

const SIGNAL_LABELS = {
  HEAVY_CANOPY: 'Heavy canopy',
  MATURE_TREES: 'Mature trees',
  MOSTLY_OPEN: 'Mostly open',
  CANOPY_OVER_STRUCTURE: 'Canopy over structure',
  NO_PARCEL_POLYGON: 'No parcel polygon',
};

/** Pill tone for a signal tone; unknown tones fall back to neutral. */
export function signalTone(tone) {
  if (tone === 'good' || tone === 'positive') return 'good';
  if (tone === 'warning' || tone === 'critical' || tone === 'info') return tone;
  return 'neutral';
}

export function signalLabel(code) {
  if (!code) return 'Signal';
  return SIGNAL_LABELS[code] || String(code).toLowerCase().replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

const metres = (v, digits = 0) => (isNum(v) ? `${fmtNum(v, digits)} m` : DASH);
const feet = (m) => (isNum(m) ? `${fmtNum(m * 3.28084)} ft` : null);

/** One sentence for the surround block: cover in the ring, nearest tall canopy, crowns on the line. */
export function surroundSentence(surround) {
  if (!surround) return null;
  const radius = isNum(surround.radius_ft) ? `${fmtNum(surround.radius_ft)} ft` : 'the search radius';
  const parts = [];
  parts.push(`Within ${radius}: ${isNum(surround.surround_cover_pct) ? fmtPct(surround.surround_cover_pct, 0) : DASH} canopy`);
  if (isNum(surround.nearest_tall_canopy_ft)) parts.push(`nearest tall tree ${fmtNum(surround.nearest_tall_canopy_ft)} ft from the line`);
  else if (surround.nearest_tall_canopy_ft === null) parts.push(`no tall canopy within ${radius}`);
  const touching = surround.patches_touching_boundary;
  if (isNum(touching)) parts.push(touching === 0 ? 'no crowns on the boundary' : `${fmtNum(touching)} crown${touching === 1 ? '' : 's'} on the boundary`);
  return parts.join(' · ');
}

function HeightClassBar({ classes }) {
  const pct = (key) => (isNum(classes?.[key]) ? Math.max(0, classes[key]) : null);
  const known = HEIGHT_CLASSES.map((c) => pct(c.key)).filter((v) => v !== null);
  if (!known.length) return null;
  const total = known.reduce((s, v) => s + v, 0) || 100;
  return (
    <div className="le-canopy-classes">
      <div className="le-canopy-bar" role="img" aria-label={`Canopy by height: ${HEIGHT_CLASSES.map((c) => `${c.label} ${c.name} ${fmtPct(pct(c.key), 0)}`).join(', ')}`}>
        {HEIGHT_CLASSES.map((c) => {
          const v = pct(c.key);
          if (!v) return null;
          return <span key={c.key} className="le-canopy-bar-seg" style={{ width: `${(v / total) * 100}%`, background: c.color }} title={`${c.label} · ${c.name} · ${fmtPct(v, 0)}`} />;
        })}
      </div>
      <div className="le-canopy-class-legend">
        {HEIGHT_CLASSES.map((c) => (
          <span key={c.key} className="le-canopy-class" title={`Share of the parcel's canopy area between ${c.label} tall`}>
            <span className="le-canopy-class-swatch" style={{ background: c.color }} />
            <span className="le-canopy-class-label">{c.label} <span className="muted">{c.name}</span></span>
            <span className="le-canopy-class-value">{fmtPct(pct(c.key), 0)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function TreeCanopyCard({ data, status }) {
  const summary = data?.summary || {};
  const scope = data?.scope || {};
  const source = scope.canopy_source || {};
  const surround = summary.surround || null;
  const signals = safeArray(data?.signals);
  const notes = safeArray(data?.notes);
  const minHeight = scope.tree_definition?.min_height_m;
  const subtitle = data
    ? `1 m canopy-height model · trees ≥ ${isNum(minHeight) ? minHeight : 3} m tall · canopy patches, not tree counts${isNum(scope.imagery_as_of) ? ` · imagery ${scope.imagery_as_of}` : ''}`
    : null;
  const coverSub = [
    isNum(summary.tree_canopy_area_sqft) ? `${fmtSqft(summary.tree_canopy_area_sqft)} of canopy` : null,
    isNum(scope.parcel_area_sqft) ? `on a ${fmtSqft(scope.parcel_area_sqft)} parcel` : null,
    isNum(scope.imagery_as_of) ? `as of ${scope.imagery_as_of}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <SectionCard id="le-section-canopy" icon={<MdOutlineForest size={20} />} title="Tree canopy" subtitle={subtitle} status={status} dataset={data}>
      {data ? (
        <>
          <div className="le-stat-row le-stat-row-hero">
            <StatTile label="Tree cover" value={fmtPct(summary.tree_cover_pct, 1)} sub={coverSub || null} size="lg" emphasis hint="Share of the parcel under canopy at least 3 m tall" />
            <StatTile label="Tallest canopy" value={metres(summary.max_height_m)} sub={feet(summary.max_height_m)} hint="Highest canopy-height pixel on the parcel" />
            <StatTile label="Mean height" value={metres(summary.mean_height_m, 1)} sub={feet(summary.mean_height_m)} hint="Mean height across the parcel's canopy pixels" />
            <StatTile label="Canopy patches" value={isNum(summary.canopy_patches) ? fmtNum(summary.canopy_patches) : DASH} sub={isNum(summary.largest_patch_sqft) ? `largest ${fmtSqft(summary.largest_patch_sqft)}` : null} hint="Connected canopy patches of at least 10 m² — one patch can be several touching crowns" />
            {isNum(summary.tree_cover_open_ground_pct) ? (
              <StatTile label="Open-ground tree cover" value={fmtPct(summary.tree_cover_open_ground_pct, 1)} sub="canopy over the unbuilt part of the lot" hint="Share of the parcel outside the building footprints that is under canopy (needs building footprints loaded for the county)" />
            ) : null}
          </div>

          <HeightClassBar classes={summary.height_classes_pct} />

          {surround ? (
            <div className="le-canopy-surround" title="Canopy in the ring around the parcel, from the same model">
              <strong>Surroundings.</strong> {surroundSentence(surround)}
            </div>
          ) : null}

          {signals.length ? (
            <div className="le-canopy-signals">
              {signals.map((s) => (
                <div key={s.code || s.message} className="le-canopy-signal">
                  <Pill tone={signalTone(s.tone)}>{signalLabel(s.code)}</Pill>
                  {s.message ? <span className="le-canopy-signal-text">{s.message}</span> : null}
                </div>
              ))}
            </div>
          ) : null}

          <Note tone="info">
            <strong>Canopy patches, not tree counts.</strong> A patch is connected canopy at least {isNum(scope.tree_definition?.min_patch_sqm) ? scope.tree_definition.min_patch_sqm : 10} m² and {isNum(minHeight) ? minHeight : 3} m tall; one patch can be one crown or several touching crowns, and crowns over the parcel can be rooted next door.
            {notes.length ? <ul className="le-warning-list">{notes.map((n) => <li key={n}>{n}</li>)}</ul> : null}
          </Note>

          <div className="le-footnotes">
            <span>{CANOPY_ATTRIBUTION}{source.version ? ` · release ${source.version}` : ''}{isNum(scope.pixel_size_m) ? ` · ${scope.pixel_size_m} m pixels` : ''}{scope.method ? ` · method ${scope.method}` : ''}{data.retrieved_at ? ` · retrieved ${fmtDateTime(data.retrieved_at)}` : ''}</span>
          </div>
        </>
      ) : null}
    </SectionCard>
  );
}
